import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminCustomerDetail, api, formatDate, formatPrice, Service } from '../../api';
import Footer from '../../Components/Footer';
import { Alert, Loading, StatusTag, useAction, useLoad } from '../../Components/ui';

const TYPE_LABEL: Record<Service['type'], string> = {
    wordpress: 'WordPress', static: 'Static', domain: 'Domain', dns: 'DNS', database: 'Database',
};

function Kv({ label, children }: { label: string; children: React.ReactNode }) {
    return <div><div className="kv-label">{label}</div><div className="kv-value">{children}</div></div>;
}

/** /admin/customers/:id — one customer's setup: services (with admin actions) and orders. */
export default function AdminCustomer() {
    const { customerId } = useParams();
    const { data, error, loading, reload } = useLoad(
        () => api.get<AdminCustomerDetail>(`/admin/customers/${customerId}`).then(r => r.data), [customerId]);
    const action = useAction();
    const [notice, setNotice] = useState<string | null>(null);

    // Suspend / reactivate / terminate through CarbonEdge (PUT /v1/services/:id/status).
    const setStatus = (service: Service, status: 'active' | 'suspended' | 'terminated') => {
        const verb = { active: 'Reactivate', suspended: 'Suspend', terminated: 'Terminate' }[status];
        const warning = status === 'terminated' ? ' This cannot be undone and removes the service in CarbonEdge.' : '';
        if (!window.confirm(`${verb} ${service.domain}?${warning}`)) return;
        setNotice(null);
        void action.run(async () => {
            const res = await api.put<{ notice: string | null }>(`/services/${service.id}/status`, { status });
            setNotice(res.data.notice);
            await reload();
        });
    };

    if (loading && !data) return <section className="section page-section"><Loading /></section>;
    if (error || !data) {
        return (
            <section className="section page-section">
                <Alert type="error">{error ?? 'Customer not found'}</Alert>
                <Link className="btn-outline" to="/admin">Back to customers</Link>
            </section>
        );
    }
    const { customer, services, orders } = data;

    return (
        <>
            <section className="section page-section">
                <span className="section-label"><Link className="text-link" to="/admin">Admin</Link> / Customer</span>
                <h1 className="page-title">{customer.name}</h1>

                <div className="panel-card" style={{ marginBottom: '1.5rem' }}>
                    <div className="kv-grid">
                        <Kv label="Email"><a className="text-link" href={`mailto:${customer.email}`}>{customer.email}</a></Kv>
                        <Kv label="Phone">{customer.phone ?? '—'}</Kv>
                        <Kv label="Role">{customer.role}</Kv>
                        <Kv label="Customer since">{formatDate(customer.createdAt)}</Kv>
                        <Kv label="Last login">{formatDate(customer.lastLoginAt)}</Kv>
                        <Kv label="CarbonEdge customer"><span className="mono">{customer.carbonEdgeCustomerId ?? 'not created yet'}</span></Kv>
                    </div>
                </div>

                <h3 className="sub-title">Services</h3>
                {action.error && <Alert type="error">{action.error}</Alert>}
                {notice && <Alert type="info">{notice}</Alert>}
                <div className="table-wrap" style={{ marginBottom: '1rem' }}>
                    <table className="data-table">
                        <thead><tr><th>Domain</th><th>Type</th><th>Product</th><th>Status</th><th>Renews</th><th /></tr></thead>
                        <tbody>
                            {services.map(s => (
                                <tr key={s.id}>
                                    <td className="mono">{s.domain}</td>
                                    <td>{TYPE_LABEL[s.type]}</td>
                                    <td>{s.productName}</td>
                                    <td>
                                        <StatusTag status={s.status} />
                                        {s.transfer && s.status === 'provisioning' && <span className="card-text" style={{ marginLeft: '0.5rem' }}>transfer {s.transfer.status.replace('_', ' ')}</span>}
                                        {s.lastError && s.status === 'failed' && <div className="card-text" style={{ fontSize: '0.75rem', color: '#f87171' }}>{s.lastError}</div>}
                                    </td>
                                    <td>{formatDate(s.expiresAt)}</td>
                                    <td className="actions">
                                        <Link className="btn-outline small" to={`/panel/${s.id}`}>Open panel</Link>
                                        {/* Extra databases follow their web package; they can only be terminated on their own. */}
                                        {s.status === 'active' && s.type !== 'database' && <button className="btn-outline small" disabled={action.busy} onClick={() => setStatus(s, 'suspended')}>Suspend</button>}
                                        {s.status === 'suspended' && s.type !== 'database' && <button className="btn-outline small" disabled={action.busy} onClick={() => setStatus(s, 'active')}>Reactivate</button>}
                                        {(s.status === 'active' || s.status === 'suspended') && (
                                            <button className="btn-outline small danger" disabled={action.busy} onClick={() => setStatus(s, 'terminated')}>Terminate</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {services.length === 0 && <tr><td colSpan={6} className="empty-state">No services.</td></tr>}
                        </tbody>
                    </table>
                </div>

                <h3 className="sub-title">Orders</h3>
                <div className="table-wrap">
                    <table className="data-table">
                        <thead><tr><th>Order</th><th>Product</th><th>Domain</th><th>Date</th><th>Amount</th><th>Order</th><th>Payment</th></tr></thead>
                        <tbody>
                            {orders.map(o => (
                                <tr key={o.id}>
                                    <td className="mono">{o.id.slice(0, 8).toUpperCase()}</td>
                                    <td>{o.productName}</td>
                                    <td>{o.domain}</td>
                                    <td>{formatDate(o.createdAt)}</td>
                                    <td>{formatPrice(o.amountOre)}</td>
                                    <td><StatusTag status={o.status} /></td>
                                    <td>
                                        {o.paymentStatus
                                            ? <><StatusTag status={o.paymentStatus} /> <span className="card-text">{o.paymentProvider}{o.paymentAttempts > 1 ? ` · ${o.paymentAttempts} attempts` : ''}</span></>
                                            : <span className="card-text">{o.amountOre === 0 ? 'free' : 'none'}</span>}
                                    </td>
                                </tr>
                            ))}
                            {orders.length === 0 && <tr><td colSpan={7} className="empty-state">No orders.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>
            <Footer />
        </>
    );
}
